import { useToast } from '../contexts/ToastContext';
import type { ToastVariant } from '../contexts/ToastContext';

const variantStyles: Record<ToastVariant, { background: string }> = {
  success: { background: 'var(--color-success)' },
  error: { background: 'var(--color-danger)' },
  warning: { background: 'var(--color-warning)' },
  info: { background: 'var(--color-primary)' },
  loading: { background: 'var(--color-primary)' },
};

function ToastIcon({ variant }: { variant: ToastVariant }) {
  if (variant === 'loading') {
    return (
      <svg className="toast-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
        <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
      </svg>
    );
  }
  if (variant === 'success') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  if (variant === 'error') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    );
  }
  if (variant === 'warning') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  // info
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div
          key={t.id}
          style={{
            ...variantStyles[t.variant],
            color: 'white',
            padding: '14px 20px',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 600,
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            maxWidth: '420px',
            animation: t.removing ? 'toast-slide-out 0.3s ease-in forwards' : 'toast-slide-in 0.3s ease-out',
            pointerEvents: 'auto',
          }}
        >
          <ToastIcon variant={t.variant} />
          <span style={{ flex: 1 }}>{t.message}</span>
          {t.variant !== 'loading' && (
            <button
              onClick={() => removeToast(t.id)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '2px',
                opacity: 0.7,
                display: 'flex',
                flexShrink: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function ConfirmDialog() {
  const { pendingConfirm, resolveConfirm } = useToast();

  if (!pendingConfirm) return null;

  const { title, description, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive } = pendingConfirm;

  return (
    <div className="modal-overlay" onClick={() => resolveConfirm(false)} onKeyDown={e => { if (e.key === 'Escape') resolveConfirm(false); }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--color-text-primary)' }}>{title}</h3>
        </div>
        <div className="modal-body">
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{description}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={() => resolveConfirm(false)}>{cancelLabel}</button>
          <button
            className={destructive ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={() => resolveConfirm(true)}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
